/**
 * ReadReceiptAvatar — own-message-only read-receipt indicator that hugs the
 * right edge of each own bubble row.
 *
 * Flutter source of truth: `currentUserMessageStatus`
 * (`chat_screen.dart:2252-2293`) and the position helper
 * `myLastMessageReadPosition` (`chat_screen.dart:760-771`).
 *
 * Behavior (matches Flutter line-for-line) is driven by the precomputed
 * `mode` prop (`ReadReceiptMode`):
 *  - `failed`         → 16px error icon, color `theme.disabled`
 *    (Flutter `Icons.error` at `chat_screen.dart:2256`).
 *  - `notYetSeen`     → 16px check-circle, color `theme.disabled`
 *    (Flutter `Icons.check_circle` at `chat_screen.dart:2260-2264`). Also fires
 *    when no own message has been seen yet
 *    (`myLastMessageReadPosition === -1`, `chat_screen.dart:2259`).
 *  - `lastReadAvatar` → 16px circular peer avatar
 *    (`chat_screen.dart:2272-2289`). If `peerUser` is `null` (peer not yet
 *    fetched) Flutter renders an empty 16×16 box at
 *    `chat_screen.dart:2270`; we do the same.
 *  - `spacer`         → 16×16 spacer to preserve the row width on every other
 *    own bubble (`chat_screen.dart:2292`).
 *
 * This component is rendered by `MessageBubble` to the RIGHT of every OWN
 * bubble (`crossAxisAlignment: end` in Flutter's `Row` at
 * `chat_screen.dart:1348-1359` → the receipt sits at the bottom-right of the
 * row). Peer rows render `PeerProfileAvatar` instead.
 *
 * Theme / responsive tokens:
 *  - Size                  → `responsive.icon.small` (= 16, exact Flutter parity).
 *  - Disabled tint         → `theme.disabled` (Flutter `AppColors.disabledColor`).
 *  - Border radius (round) → `responsive.borderRadius.round` for the avatar.
 *
 * The "which mode does this bubble belong to" decision is made by the caller
 * (`ChatMessagesList`) via `ChatUtils.getReadReceiptMode(...)` so this
 * component stays a pure renderer and the per-bubble React reconciliation does
 * not depend on the whole `messages` array.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { ReadReceiptMode } from '@/domain/enums/chat/readReceiptMode';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface ReadReceiptAvatarProps {
  /**
   * Precomputed mode for THIS bubble — produced once by
   * `ChatUtils.getReadReceiptMode` in `ChatMessagesList.renderItem`.
   */
  mode: ReadReceiptMode;
  /** Peer user, or `null` if not yet fetched. */
  peerUser: UserEntity | null;
  /**
   * `data-qa-*` set for the rendered receipt node so the qa-tester can assert
   * a message's read state. The caller (`MessageBubble`) builds the id as
   * `chat-read-receipt-<messageId>`; the `lastReadAvatar` mode (peer avatar
   * shown) carries `status: 'read'`.
   */
  qa?: QaAttrOptions;
}

export const ReadReceiptAvatar: React.FC<ReadReceiptAvatarProps> = ({
  mode,
  peerUser,
  qa,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const size = responsive.icon.small;
  // Raw `data-qa-*` spread for the bare-`<div>` branches (spacer / empty slot).
  const qaProps = qa ? qaAttr(qa) : {};

  // Failed-send branch (`chat_screen.dart:2255-2257`).
  if (mode === ReadReceiptMode.failed) {
    return (
      <AssetIcon
        qa={qa}
        path="/assets/images/error.png"
        width={size}
        height={size}
        color={theme.disabled}
      />
    );
  }

  // No own message seen yet, or this bubble sits above the last read one
  // (`chat_screen.dart:2259-2265`).
  if (mode === ReadReceiptMode.notYetSeen) {
    return (
      <AssetIcon
        qa={qa}
        path="/assets/images/check.png"
        width={size}
        height={size}
        color={theme.disabled}
      />
    );
  }

  // This bubble IS the most-recent own message the peer has read
  // (`chat_screen.dart:2267-2289`).
  if (mode === ReadReceiptMode.lastReadAvatar) {
    if (peerUser === null) {
      // Peer not yet fetched — render the same fixed-size empty slot Flutter
      // does at `chat_screen.dart:2270` so the row width stays stable.
      return <div {...qaProps} style={{ width: size, height: size }} />;
    }
    return (
      // `CachedImage` derives its own `data-qa-status` from load state, so the
      // `read` assertion the agent relies on lives on the bubble root
      // (`MessageBubble` emits `data-qa-status="read"`); here the id alone
      // (`chat-read-receipt-<messageId>`) marks the receipt node's presence.
      <CachedImage
        qaId={qa?.id}
        src={UserUtils.generateUserProfileImageUrl(peerUser.firebaseId)}
        alt=""
        style={{
          width: size,
          height: size,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    );
  }

  // Bubble is below the last-read mark — render an empty spacer to keep the
  // row width consistent (`chat_screen.dart:2292`).
  return <div {...qaProps} style={{ width: size, height: size }} />;
};
